import { useEffect, useMemo, useRef, useState } from 'react';
import type { DroneMissionConfig } from '../constants/dronePrescriptionLayers';
import {
  DRONE_INDEX_DEFS,
  DRONE_ORTHO_BANDS,
  availableOrthoIndexes,
  buildOrthoIndexTileUrl,
  rateIndexParts,
  type DroneIndexKey,
} from '../constants/droneOrthoBands';
import {
  fieldOverlapsDroneMission,
  formatDroneOrthoReportText,
  runDroneOrthoFieldAnalysis,
  runSingleDroneOrthoIndex,
  type DroneOrthoAnalysisState,
  type DroneOrthoFieldReport,
} from '../utils/droneOrthoAnalytics';
import {
  downloadDroneOrthoFullReportPdf,
  downloadDroneOrthoReportPdf,
} from '../utils/droneOrthoReportPdf';
import { cleanDroneIndexPreviewToDataUrl } from '../utils/droneIndexPreviewClean';
import digisakaLogo from '../../../assets/digisaka-official-logo.png';

type FarmDroneOrthoAnalysisProps = {
  isDark: boolean;
  isCompact?: boolean;
  mission: DroneMissionConfig;
  fieldGeoJson?: Record<string, unknown> | null;
  farmName?: string | null;
  /** Show computed index colormap on the map. */
  onShowIndexMap?: (
    mission: DroneMissionConfig,
    indexKey: DroneIndexKey,
    tileUrl: string
  ) => void;
  onClearIndexMap?: () => void;
};

export default function FarmDroneOrthoAnalysis({
  isDark,
  isCompact = false,
  mission,
  fieldGeoJson,
  farmName,
  onShowIndexMap,
  onClearIndexMap,
}: FarmDroneOrthoAnalysisProps) {
  const [selectedIndex, setSelectedIndex] = useState<DroneIndexKey>('ndvi');
  const [state, setState] = useState<DroneOrthoAnalysisState>({ status: 'idle' });
  const [pdfBusy, setPdfBusy] = useState(false);
  const [fullReportBusy, setFullReportBusy] = useState(false);
  const [fullReportProgress, setFullReportProgress] = useState<string | null>(null);
  const [fullReportError, setFullReportError] = useState<string | null>(null);
  const [cleanPreviewUrl, setCleanPreviewUrl] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fullReportAbortRef = useRef<AbortController | null>(null);
  const previewAbortRef = useRef<AbortController | null>(null);

  const overlap = useMemo(
    () => fieldOverlapsDroneMission(fieldGeoJson ?? null, mission),
    [fieldGeoJson, mission]
  );

  const bands = DRONE_ORTHO_BANDS[mission.key];
  const indexOptions = useMemo(() => availableOrthoIndexes(bands), [bands]);

  const textXs = isCompact ? 'text-[10px]' : 'text-xs';
  const textSm = isCompact ? 'text-[11px]' : 'text-sm';

  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    fullReportAbortRef.current?.abort();
    fullReportAbortRef.current = null;
    previewAbortRef.current?.abort();
    previewAbortRef.current = null;
    setState({ status: 'idle' });
    setCleanPreviewUrl(null);
    setFullReportBusy(false);
    setFullReportProgress(null);
    setFullReportError(null);
    onClearIndexMap?.();
    // Prefer NDVI when available for this mission
    setSelectedIndex(availableOrthoIndexes(DRONE_ORTHO_BANDS[mission.key])[0]?.key ?? 'ndvi');
  }, [mission.key, fieldGeoJson]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.status !== 'loaded' || !state.report.previewUrl) {
      setCleanPreviewUrl(null);
      return;
    }
    previewAbortRef.current?.abort();
    const ac = new AbortController();
    previewAbortRef.current = ac;
    setCleanPreviewUrl(null);
    cleanDroneIndexPreviewToDataUrl(state.report.previewUrl, ac.signal).then((url) => {
      if (!ac.signal.aborted) setCleanPreviewUrl(url);
    });
    return () => ac.abort();
  }, [state]);

  const runSelectedIndex = async () => {
    if (!fieldGeoJson) {
      setState({ status: 'no_field' });
      return;
    }
    if (!overlap.overlaps) {
      setState({ status: 'no_overlap', missionLabel: mission.label });
      return;
    }

    const def = DRONE_INDEX_DEFS.find((d) => d.key === selectedIndex);
    if (def?.requiresRedEdge && bands.redEdge == null) {
      setState({
        status: 'error',
        message: 'Leaf richness is not available on this drone map. Try Plant greenness instead.',
      });
      return;
    }

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setState({ status: 'loading', progress: 0, total: 1 });

    try {
      const report = await runSingleDroneOrthoIndex(
        mission,
        fieldGeoJson,
        farmName,
        selectedIndex,
        ac.signal
      );
      if (ac.signal.aborted) return;

      const tileUrl = buildOrthoIndexTileUrl(mission.orthoCogUrl, selectedIndex, bands);
      if (tileUrl) {
        onShowIndexMap?.(mission, selectedIndex, tileUrl);
      }

      setState({ status: 'loaded', report });
    } catch (err) {
      if (ac.signal.aborted) return;
      onClearIndexMap?.();
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Could not analyse this field.',
      });
    }
  };

  const downloadTxt = (report: DroneOrthoFieldReport) => {
    const blob = new Blob([formatDroneOrthoReportText(report)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Digisaka_field_summary_${(farmName || mission.key).replace(/[^\w.-]+/g, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const card = isDark ? 'border-cyan-700/50 bg-cyan-950/20' : 'border-cyan-200 bg-cyan-50/70';
  const muted = isDark ? 'text-cyan-200/80' : 'text-cyan-900/75';
  const heading = isDark ? 'text-cyan-100' : 'text-cyan-950';

  const loadedIdx =
    state.status === 'loaded'
      ? state.report.indexes.find((i) => i.available) ?? state.report.indexes[0]
      : null;
  const loadedDef = loadedIdx ? DRONE_INDEX_DEFS.find((d) => d.key === loadedIdx.key) : null;
  const loadedRating =
    loadedIdx?.available != null && loadedIdx.available
      ? rateIndexParts(loadedIdx.key, loadedIdx.mean)
      : null;

  const handlePdf = async (report: DroneOrthoFieldReport) => {
    setPdfBusy(true);
    try {
      await downloadDroneOrthoReportPdf(report);
    } finally {
      setPdfBusy(false);
    }
  };

  /** All indexes in one PDF — no index selection required. */
  const generateFullReport = async () => {
    if (!fieldGeoJson || !overlap.overlaps) return;

    fullReportAbortRef.current?.abort();
    const ac = new AbortController();
    fullReportAbortRef.current = ac;
    setFullReportBusy(true);
    setFullReportError(null);
    setFullReportProgress('Running all crop checks…');

    try {
      const report = await runDroneOrthoFieldAnalysis(mission, fieldGeoJson, farmName, {
        signal: ac.signal,
        onProgress: (done, total) => {
          if (!ac.signal.aborted) {
            setFullReportProgress(`Running checks ${done}/${total}…`);
          }
        },
      });
      if (ac.signal.aborted) return;
      setFullReportProgress('Building PDF…');
      await downloadDroneOrthoFullReportPdf(report, mission);
      if (!ac.signal.aborted) setFullReportProgress(null);
    } catch (err) {
      if (ac.signal.aborted) return;
      setFullReportError(
        err instanceof Error ? err.message : 'Could not generate the full field report.'
      );
      setFullReportProgress(null);
    } finally {
      if (!ac.signal.aborted) setFullReportBusy(false);
    }
  };

  return (
    <div className={`mt-2 rounded-lg border ${isCompact ? 'p-2' : 'p-3'} ${card}`}>
      <div className="mb-1.5 flex items-start gap-2">
        <span
          className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            isDark ? 'bg-cyan-900/60 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
          }`}
        >
          <i className="ri-map-pin-range-line" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`font-semibold ${textSm} ${heading}`}>Check crop on drone map</p>
          <p className={`mt-0.5 leading-snug ${textXs} ${muted}`}>
            Download a full report of every crop check, or pick one check to run on the map and
            download a single-index summary.
          </p>
        </div>
      </div>

      {!fieldGeoJson ? (
        <p className={`${textXs} ${muted}`}>Select a field on the map first.</p>
      ) : !overlap.overlaps ? (
        <p className={`${textXs} ${isDark ? 'text-amber-200/90' : 'text-amber-900/80'}`}>
          This field is outside the <strong>{mission.label}</strong> drone map. Choose a field inside
          the coverage, or request a new drone flight.
        </p>
      ) : (
        <>
          <p className={`mb-2 ${textXs} ${isDark ? 'text-emerald-300' : 'text-emerald-800'}`}>
            <i className="ri-checkbox-circle-line mr-1" aria-hidden />
            Your field is inside {mission.label}.
          </p>

          <button
            type="button"
            onClick={generateFullReport}
            disabled={fullReportBusy || state.status === 'loading'}
            className={`mb-1.5 w-full rounded-lg px-3 py-2 font-semibold text-white transition-colors disabled:opacity-60 ${textXs} ${
              isDark ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-[#008540] hover:opacity-90'
            }`}
          >
            <i className="ri-file-pdf-2-line mr-1" aria-hidden />
            {fullReportBusy
              ? fullReportProgress || 'Generating…'
              : 'Generate report (all checks)'}
          </button>
          <p className={`mb-2 leading-snug ${textXs} ${muted}`}>
            One PDF with every available index analysis for this field. No map check needed first.
          </p>
          {fullReportError ? (
            <p
              className={`mb-2 rounded-md px-2 py-1.5 ${textXs} ${
                isDark ? 'bg-red-950/40 text-red-300' : 'bg-red-50 text-red-700'
              }`}
            >
              {fullReportError}
            </p>
          ) : null}

          <p className={`mb-1 font-medium ${textXs} ${heading}`}>Or choose one check for the map</p>
          <div className={`mb-2 grid grid-cols-1 gap-1`}>
            {indexOptions.map((opt) => {
              const selected = selectedIndex === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setSelectedIndex(opt.key);
                    setState({ status: 'idle' });
                  }}
                  className={`rounded-lg border px-2 py-1.5 text-left transition-colors ${textXs} ${
                    selected
                      ? isDark
                        ? 'border-cyan-400 bg-cyan-900/40 text-cyan-50'
                        : 'border-cyan-500 bg-white text-cyan-950 shadow-sm'
                      : isDark
                        ? 'border-gray-600 bg-gray-900/30 text-gray-200 hover:border-cyan-600'
                        : 'border-gray-200 bg-white text-gray-800 hover:border-cyan-300'
                  }`}
                >
                  <span className="font-semibold">{opt.plainLabel}</span>
                  <span className={`ml-1 opacity-70`}>({opt.label})</span>
                  <span className={`mt-0.5 block leading-snug ${muted}`}>{opt.plainDescription}</span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={runSelectedIndex}
            disabled={state.status === 'loading' || fullReportBusy}
            className={`mb-2 w-full rounded-lg px-3 py-2 font-semibold text-white transition-colors disabled:opacity-60 ${textXs} ${
              isDark ? 'bg-cyan-700 hover:bg-cyan-600' : 'bg-[#0e7490] hover:bg-[#0f766e]'
            }`}
          >
            {state.status === 'loading' ? 'Running…' : 'Run this check & show on map'}
          </button>
        </>
      )}

      {state.status === 'error' && (
        <p
          className={`rounded-md px-2 py-1.5 ${textXs} ${
            isDark ? 'bg-red-950/40 text-red-300' : 'bg-red-50 text-red-700'
          }`}
        >
          {state.message}
        </p>
      )}

      {state.status === 'loaded' && loadedIdx?.available && loadedDef && loadedRating && (
        <div
          className={`overflow-hidden rounded-xl border ${textXs} ${
            isDark ? 'border-gray-700 bg-gray-950/40' : 'border-gray-200 bg-white'
          }`}
        >
          {/* Header — Digisaka brand lockup */}
          <div className="bg-[#008540] px-3 py-2.5 text-white">
            <div className="flex items-center gap-2.5">
              <img
                src={digisakaLogo}
                alt="Digisaka"
                className="h-9 w-auto shrink-0 object-contain object-left"
              />
              <p className="text-[11px] text-white/90 leading-snug">Field crop health summary</p>
            </div>
          </div>
          <div
            className={`border-b px-3 py-1.5 text-[10px] ${
              isDark
                ? 'border-gray-700 bg-gray-900/60 text-gray-300'
                : 'border-gray-200 bg-[#f1f5f2] text-gray-600'
            }`}
          >
            {new Date(state.report.generatedAt).toLocaleDateString()} ·{' '}
            {state.report.farmName || 'Your field'} · about {state.report.fieldAreaHa.toFixed(2)} ha
            · {state.report.missionLabel}
          </div>

          <div className="space-y-2.5 p-2.5">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <p className={`text-[10px] font-medium ${muted}`}>What we checked</p>
                <p className={`text-[12px] font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {loadedDef.plainLabel}
                </p>
                <p className={`mt-0.5 leading-snug ${muted}`}>{loadedDef.plainDescription}</p>
                <div
                  className={`mt-2 rounded-xl px-2.5 py-2 ${
                    isDark ? 'bg-emerald-950/50' : 'bg-[#e8f3ec]'
                  }`}
                >
                  <p className={`text-[10px] font-medium ${isDark ? 'text-emerald-300' : 'text-[#0d5029]'}`}>
                    Result
                  </p>
                  <p className={`text-base font-semibold ${isDark ? 'text-emerald-200' : 'text-[#0d5029]'}`}>
                    {loadedRating.level}
                  </p>
                  <p className={`text-[10px] ${isDark ? 'text-emerald-300/90' : 'text-[#0d5029]'}`}>
                    {loadedRating.detail}
                  </p>
                </div>
                <div className={`mt-1.5 flex flex-wrap gap-3 ${muted}`}>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full bg-[#2f9e5a]" />
                    Healthier
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full bg-[#e0a020]" />
                    Weaker spots
                  </span>
                </div>
                <div
                  className={`mt-2 grid grid-cols-5 gap-1 rounded-lg border px-1.5 py-1.5 ${
                    isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-100 bg-gray-50'
                  }`}
                >
                  {(
                    [
                      ['Avg', loadedIdx.mean.toFixed(3)],
                      ['Min', loadedIdx.min.toFixed(3)],
                      ['Max', loadedIdx.max.toFixed(3)],
                      ['Std', loadedIdx.std != null ? loadedIdx.std.toFixed(3) : '—'],
                      ['Cover', `${loadedIdx.validPercent.toFixed(0)}%`],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="min-w-0 text-center">
                      <p className={`text-[9px] ${muted}`}>{label}</p>
                      <p
                        className={`truncate text-[10px] font-semibold ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="overflow-hidden rounded-xl bg-black">
                  {cleanPreviewUrl || state.report.previewUrl ? (
                    <img
                      src={cleanPreviewUrl || state.report.previewUrl!}
                      alt={`${loadedDef.plainLabel} on your field`}
                      className="h-28 w-full object-contain bg-black"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-28 items-center justify-center bg-black text-[10px] text-white/60">
                      Field drone preview
                    </div>
                  )}
                </div>
                <p className={`mt-1 text-center text-[10px] ${muted}`}>
                  Colored areas are your crop check on the map
                </p>
              </div>
            </div>

            <div className={`border-t pt-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`mb-1.5 font-semibold ${heading}`}>Simple field insights</p>
              <div className="grid grid-cols-1 gap-1.5">
                <p className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                  <i className="ri-leaf-line mr-1 text-[#008540]" aria-hidden />
                  {state.report.parameters.canopyCoverPercent != null
                    ? `About ${state.report.parameters.canopyCoverPercent.toFixed(0)}% of the field looks well covered with plants.`
                    : 'Plant cover not estimated for this check.'}
                </p>
                <p className={muted}>
                  <i className="ri-ruler-line mr-1" aria-hidden />
                  Plant height not available yet. Needs a special height map.
                </p>
                <p className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                  <i className="ri-plant-line mr-1 text-[#008540]" aria-hidden />
                  Leaf greenness:{' '}
                  {state.report.parameters.chlorophyllProxy != null
                    ? rateIndexParts(
                        loadedIdx.key === 'ndre' ? 'ndre' : 'ndvi',
                        state.report.parameters.chlorophyllProxy
                      ).level
                    : '—'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                disabled={pdfBusy}
                onClick={() => handlePdf(state.report)}
                className={`rounded-lg px-2 py-1.5 font-semibold text-white disabled:opacity-60 ${
                  isDark ? 'bg-emerald-700 hover:bg-emerald-600' : 'bg-[#008540] hover:opacity-90'
                }`}
              >
                <i className="ri-file-pdf-line mr-1" aria-hidden />
                {pdfBusy ? 'Preparing…' : 'PDF report'}
              </button>
              <button
                type="button"
                onClick={() => downloadTxt(state.report)}
                className={`rounded-lg px-2 py-1.5 font-medium ${
                  isDark
                    ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                    : 'bg-white text-gray-800 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <i className="ri-file-text-line mr-1" aria-hidden />
                Text report
              </button>
            </div>

            <button
              type="button"
              onClick={() => onClearIndexMap?.()}
              className={`w-full rounded-lg px-2 py-1 ${
                isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              Hide index map layer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
